<script setup lang="ts">
const runtimeConfig = useRuntimeConfig()
const route = useRoute()

const { data: post } = await useAsyncData(route.path, () =>
  queryCollection('news').path(route.path.toLowerCase()).first(),
)

const { data: allPosts } = await useAsyncData('all-news-for-related', () =>
  queryCollection('news').order('date', 'DESC').all(),
)

const MAX_RELATED = 4

const relatedPosts = computed(() => {
  if (!post.value || !allPosts.value) return []
  const seen = new Set<string>([post.value.path])
  const result: typeof allPosts.value = []

  // 1. 手動指定の related（slug で指定）
  if (post.value.related) {
    for (const slug of post.value.related) {
      const found = allPosts.value.find(p => p.path === `/news/${slug}`)
      if (found && !seen.has(found.path) && !found.draft) {
        result.push(found)
        seen.add(found.path)
      }
    }
  }

  // 2. 新しい順で埋める（news は date DESC の読み物なので、カテゴリではなく新しさで補完）
  for (const p of allPosts.value) {
    if (result.length >= MAX_RELATED) break
    if (!seen.has(p.path) && !p.draft) {
      result.push(p)
      seen.add(p.path)
    }
  }

  return result
})

useSeoMeta({
  title: post.value ? `${post.value.title} | Code & Magic` : 'Code & Magic',
  description: post.value?.description || '',
  ogTitle: post.value ? `${post.value.title} | Code & Magic` : 'Code & Magic',
  ogDescription: post.value?.description || '',
  ogImage: post.value?.image
    ? `${runtimeConfig.public.serviceDomain}${post.value.image}`
    : `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
  ogType: 'article',
  twitterCard: 'summary_large_image',
})

useHead({
  link: [
    ...(post.value
      ? [{ rel: 'canonical', href: `${runtimeConfig.public.serviceDomain}${post.value.path}` }]
      : []),
    { rel: 'alternate', type: 'application/rss+xml', title: 'Code & Magic ニュース RSS', href: '/feed.xml' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
    { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;500;700;800&display=swap' },
  ],
})

// JSON-LD: NewsArticle
if (post.value) {
  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    'headline': post.value.title,
    'description': post.value.description,
    'datePublished': post.value.date,
    'image': post.value.image
      ? `${runtimeConfig.public.serviceDomain}${post.value.image}`
      : `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
    'publisher': {
      '@type': 'Organization',
      'name': 'サムライアプス株式会社',
      'url': 'https://www.samurai-apps.com/',
    },
  })
}

definePageMeta({
  layout: 'simple-landing',
})

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
}
</script>

<template>
  <div class="news-bg">
    <div class="centered-area">
      <div class="rounded-area">
        <NuxtLink
          to="/"
          class="home-logo"
        >
          <BrandTextLogo />
        </NuxtLink>

        <template v-if="post">
          <header class="post-header">
            <div class="post-meta">
              <time class="post-date">{{ formatDate(post.date) }}</time>
              <span
                v-if="post.category"
                class="post-category"
              >{{ post.category }}</span>
            </div>
            <h1 class="post-headline">
              {{ post.title }}
            </h1>
          </header>

          <div class="post-card">
            <img
              v-if="post.image"
              :src="post.image"
              :alt="post.imageAlt || post.title"
              class="post-hero-image"
            >
            <article class="post-body">
              <ContentRenderer :value="post" />
            </article>
          </div>

          <!-- #852/#812: 記事は外から来た人が最初に踏む面なので、読み終わりに
               「登録なしで遊べる」入口（/works）を必ず置く。 -->
          <aside class="post-cta">
            <p class="post-cta-lead">
              Code & Magic は、自分で打ち込んだコードが、そのままゲームになる Web サービスです。
            </p>
            <div class="post-cta-actions">
              <NuxtLink
                to="/works"
                class="post-cta-button post-cta-button--primary"
              >
                みんなの作品を遊んでみる（登録不要）
              </NuxtLink>
              <NuxtLink
                to="/"
                class="post-cta-button"
              >
                Code & Magic とは
              </NuxtLink>
            </div>
          </aside>
        </template>

        <template v-else>
          <div class="not-found">
            <h1>記事が見つかりません</h1>
            <p>お探しの記事は存在しないか、移動された可能性があります。</p>
          </div>
        </template>

        <div
          v-if="relatedPosts.length > 0"
          class="related-section"
        >
          <h2 class="related-heading">
            ほかのニュース
          </h2>
          <div class="related-list">
            <NuxtLink
              v-for="rel in relatedPosts"
              :key="rel.path"
              :to="rel.path"
              class="related-card"
            >
              <span class="related-date">{{ formatDate(rel.date) }}</span>
              <span class="related-title">{{ rel.title }}</span>
            </NuxtLink>
          </div>
        </div>

        <nav class="post-nav">
          <NuxtLink
            to="/news"
            class="nav-link"
          >
            ニュース一覧に戻る
          </NuxtLink>
          <NuxtLink
            to="/articles"
            class="nav-link"
          >
            遊び方のガイド
          </NuxtLink>
          <NuxtLink
            to="/"
            class="nav-link"
          >
            ホームに戻る
          </NuxtLink>
        </nav>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '~/assets/css/landing-variables.css';

.news-bg {
  font-family: "Noto Serif JP", "Noto Sans JP", serif;
  color: var(--landing-color-text);
  width: 100%;
  max-width: 100%;
  padding: 0;
}

.centered-area {
  width: 100%;
  max-width: var(--landing-max-width);
  margin: auto;
  padding: var(--landing-gutter);
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.rounded-area {
  margin: 0 0.5em;
  padding: var(--landing-rounded-padding);
  background-color: var(--landing-color-card-warm);
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow);
}

/* ---- Home logo ---- */
.home-logo {
  display: block;
  margin-top: -0.3rem;
  margin-bottom: 0;
}

.home-logo :deep(.shadowed) {
  height: 2.5rem;
  filter: none;
  opacity: 0.5;
  transition: opacity 0.2s;
}

.home-logo:hover :deep(.shadowed) {
  opacity: 0.8;
}

.post-header {
  text-align: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--landing-color-border-mint);
}

.post-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin-bottom: 0.75rem;
}

.post-date {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--landing-color-teal-dark);
}

.post-category {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--landing-color-teal-dark);
  background-color: var(--landing-color-card-mint);
  padding: 0.15rem 0.6rem;
  border-radius: 4px;
}

.post-headline {
  color: var(--landing-color-navy);
  font-size: 1.8rem;
  font-weight: 900;
  line-height: 1.5;
  margin-bottom: 0;
}

.post-card {
  background-color: #fff;
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  overflow: hidden;
}

/* ヒーローは 1200×630 で作った挿絵をそのまま置く前提なので、`articles` のように
   高さで切らない（切ると中央だけが残って絵の意図が消える）。 */
.post-hero-image {
  width: 100%;
  height: auto;
  display: block;
}

.post-body {
  padding: 1.5rem 2rem;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.7;
}

.post-body :deep(h1) {
  display: none;
}

.post-body :deep(h2) {
  font-size: 1.25rem;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.3rem;
  border-bottom: 2px solid var(--landing-color-border-mint);
}

.post-body :deep(h2 a),
.post-body :deep(h3 a) {
  color: var(--landing-color-text);
  text-decoration: none;
}

.post-body :deep(h3) {
  font-size: 1.1rem;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.post-body :deep(p) {
  margin-bottom: 0.85rem;
}

.post-body :deep(ul),
.post-body :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.post-body :deep(li) {
  margin-bottom: 0.3rem;
}

.post-body :deep(strong) {
  color: var(--landing-color-navy);
  font-weight: 900;
}

.post-body :deep(a) {
  color: var(--landing-color-teal-darker);
  font-weight: 700;
}

.post-body :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  display: block;
  margin: 1rem auto;
}

.post-body :deep(blockquote) {
  margin: 1rem 0;
  padding: 0.6rem 1rem;
  border-left: 4px solid var(--landing-color-border-mint);
  background: rgba(214, 229, 225, 0.28);
  color: #444;
}

.post-body :deep(blockquote p:last-child) {
  margin-bottom: 0;
}

.post-body :deep(pre) {
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  background: rgba(36, 52, 71, 0.06);
  border-radius: 4px;
  overflow-x: auto;
}

.post-body :deep(code) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9em;
}

/* ---- 読み終わりの CTA ---- */
.post-cta {
  margin-top: 1.5rem;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #2c3e5a 0%, #1f2c44 100%);
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  text-align: center;
}

.post-cta-lead {
  color: #f4e4bf;
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 1rem;
}

.post-cta-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
}

.post-cta-button {
  display: inline-block;
  padding: 0.6rem 1.4rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  color: #f4e4bf;
  border: 2px solid #c9a440;
  transition: background-color 0.2s, color 0.2s;
}

.post-cta-button:hover {
  background-color: rgba(201, 164, 64, 0.2);
}

.post-cta-button--primary {
  background-color: #c9a440;
  color: #1f2c44;
}

.post-cta-button--primary:hover {
  background-color: #dbb655;
  color: #1f2c44;
}

.related-section {
  margin-top: 2rem;
}

.related-heading {
  font-size: 1.1rem;
  font-weight: 900;
  color: var(--landing-color-navy);
  margin-bottom: 0.8rem;
  padding-bottom: 0.3rem;
  border-bottom: 2px solid var(--landing-color-border-mint);
}

.related-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.related-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: #fff;
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: var(--landing-color-text);
  transition: box-shadow 0.2s, transform 0.2s;
}

.related-card:hover {
  box-shadow: var(--landing-shadow);
  transform: translateY(-1px);
}

.related-date {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--landing-color-teal-dark);
}

.related-title {
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.4;
}

.not-found {
  text-align: center;
  padding: 4rem 1rem;
}

.post-nav {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--landing-color-border-mint);
}

.nav-link {
  color: var(--landing-color-teal-dark);
  text-decoration: none;
  font-size: 0.85rem;
}

.nav-link:hover {
  text-decoration: underline;
}
</style>
